<script setup lang="ts">
import { toRefs, ref, computed, watch } from 'vue';
import { useDisplay } from 'vuetify';
import { useA11yLiveFeedback } from '../composables/useA11yLiveFeedback';
import { AudioResource, SimpleKeyword, SimpleDescription, SelectedItem } from '../types';
import PlayNarrationButton from './parts/PlayNarrationButton.vue';
import ScrollableOnHover from './ScrollableOnHover.vue';

const props = defineProps<{
  t: CallableFunction;
  words: SimpleKeyword[];
  descriptions: SimpleDescription[];
  audioResources: AudioResource[];
  contentRegistryUrl: string;
  rtlLanguage: boolean;
}>();
const emit = defineEmits<{ restart: []; shuffle: []; close: [] }>();

const CORRECT_RGB = '43, 238, 51';
const INCORRECT_RGB = '233, 83, 117';
const ACTIVE_RGB = 'var(--v-theme-outline)';
const LONG_DESCRIPTION_LENGTH = 60;

const { smAndUp, mdAndUp, lgAndUp, height } = useDisplay();
const isShort = computed(() => height.value < 800);
const keywordWidth = computed(() => {
  if (lgAndUp.value) return 340;
  if (mdAndUp.value) return 300;
  return 250;
});
const { liveRegionRef: feedbackDiv, giveA11yFeedback } = useA11yLiveFeedback();
const { words, descriptions, audioResources, contentRegistryUrl, rtlLanguage } = toRefs(props);
const matches = ref<SimpleKeyword[]>([]);
const selectedItem = ref<SelectedItem>({ id: '' });
const incorrectIds = ref<string[]>([]);
const showPlayAgain = computed(() => words.value.length < 1);

watch(showPlayAgain, (val) => {
  if (val) {
    giveA11yFeedback(props.t('games.match_the_pairs.feedback.game_finished'));
  }
});

const getAudioId = (searchKey: string) => audioResources.value.find((item) => item.key === searchKey)?.id;
function matchedWord(descriptionId: string): string {
  return matches.value.find((keyword) => keyword.id === descriptionId)?.keyword ?? '';
}

const highlightError = (element: string) => {
  // Needed a timeout since the click outside handlers clear the highlight right after the click
  setTimeout(() => {
    incorrectIds.value.push(element);
  }, 10);
  setTimeout(() => {
    incorrectIds.value = incorrectIds.value.filter((id) => id !== element);
  }, 2000);
};

const clearHighlight = () => {
  incorrectIds.value = [];
};

const checkMatch = (id: string) => {
  const isMatch = id === selectedItem.value.id;
  if (isMatch) {
    matches.value.push(words?.value.find((word) => word.id === id) ?? { id: '', keyword: '' });
    const index = words?.value.findIndex((word) => word.id === id);
    // Switch focus to description element, so it won't fallback to the default top of the page when the keyword is removed from the DOM
    const descriptionElement = document.getElementById(id);
    if (descriptionElement) {
      descriptionElement.focus();
    }
    words?.value.splice(index, 1);
    selectedItem.value = { id: '' };
    giveA11yFeedback(props.t('games.match_the_pairs.feedback.correct'));
  } else {
    const descriptionId = selectedItem.value.type === 'keyword' ? id : selectedItem.value.id;
    const keywordId = selectedItem.value.type === 'keyword' ? selectedItem.value.id : id;
    highlightError(descriptionId);
    highlightError(`${keywordId}-keyword`);
    giveA11yFeedback(props.t('games.match_the_pairs.feedback.incorrect'));
  }
};

const selectableDescription = {
  click: (id: string) => {
    clearHighlight();
    if (selectedItem.value.id && selectedItem.value.type === 'keyword') {
      checkMatch(id);
    } else if (selectedItem.value.id === id && selectedItem.value.type === 'description') {
      selectedItem.value = { id: '' };
    } else {
      selectedItem.value = { id, type: 'description' };
    }
  },
  deselect: () => {
    if (selectedItem.value.type === 'description') {
      selectedItem.value = { id: '' };
    }
  },
  clickOutside: () => {
    clearHighlight();
    selectableDescription.deselect();
  },
  isActive: (id: string) => selectedItem.value.id === id && selectedItem.value.type === 'description',
};

const selectableKeyword = {
  click: (id: string) => {
    clearHighlight();
    if (selectedItem.value.id && selectedItem.value.type === 'description') {
      checkMatch(id);
    } else if (selectedItem.value.id === id && selectedItem.value.type === 'keyword') {
      selectedItem.value = { id: '' };
    } else {
      selectedItem.value = { id, type: 'keyword' };
    }
  },
  deselect: () => {
    if (selectedItem.value.type === 'keyword') {
      selectedItem.value = { id: '' };
    }
  },
  clickOutside: () => {
    clearHighlight();
    selectableKeyword.deselect();
  },
  isActive: (id: string) => selectedItem.value.id === id && selectedItem.value.type === 'keyword',
};

const onDescriptionClick = (id: string) => {
  selectableDescription.click(id);
  selectableKeyword.deselect();
};

const onKeywordClick = (id: string) => {
  selectableKeyword.click(id);
  selectableDescription.deselect();
};

const cardRingRgb = (id: string) => {
  if (matchedWord(id)) return CORRECT_RGB;
  if (incorrectIds.value.includes(id)) return INCORRECT_RGB;
  if (selectableDescription.isActive(id)) return ACTIVE_RGB;
  return undefined;
};

const keywordRingRgb = (id: string) => {
  if (incorrectIds.value.includes(`${id}-keyword`)) return INCORRECT_RGB;
  if (selectableKeyword.isActive(id)) return ACTIVE_RGB;
  return undefined;
};

const cardStyle = (id: string) => {
  const rgb = cardRingRgb(id);
  return rgb ? { borderColor: `rgb(${rgb})`, boxShadow: `0 0 0 2px rgb(${rgb})` } : undefined;
};

const keywordStyle = (id: string) => {
  const rgb = keywordRingRgb(id);
  return rgb ? { boxShadow: `0 0 0 4px rgb(${rgb})` } : undefined;
};

const clearWords = () => {
  matches.value = [];
  selectedItem.value = { id: '' };
  clearHighlight();
};

const setMatches = (newMatches: SimpleKeyword[]) => {
  matches.value = newMatches;
};

defineExpose({ clearWords, setMatches });
</script>

<template>
  <v-row
    no-gutters
    :class="{ 'overflow-hidden h-0': !smAndUp }"
    class="flex-grow-1 flex-nowrap flex-column flex-sm-row pa-sm-4 pa-md-6 ga-sm-4 ga-md-6"
  >
    <v-col cols="auto" :class="{ 'pa-3': !smAndUp }" class="d-flex flex-grow-0 order-last order-sm-first">
      <div
        v-if="!showPlayAgain"
        id="words"
        v-click-outside="selectableKeyword.clickOutside"
        role="group"
        :aria-label="t('label_keywords_list')"
        class="w-100"
      >
        <div class="d-flex flex-row flex-sm-column flex-wrap ga-3 ga-sm-10">
          <v-fade-transition group leave-absolute>
            <v-sheet
              v-for="word in words"
              :id="`${word.id}-keyword`"
              :key="word.id"
              color="inverse-surface"
              :elevation="keywordRingRgb(word.id) ? undefined : 2"
              :class="{ 'opacity-50': selectedItem.type === 'keyword' && !selectableKeyword.isActive(word.id) }"
              :style="keywordStyle(word.id)"
              class="d-flex align-center pl-4 pr-2 flex-grow-1 flex-sm-grow-0 cursor-pointer text-inverse-on-surface text-body-1 text-md-h6 text-lg-h5"
              :width="smAndUp ? keywordWidth : 'calc(50% - 6px)'"
              :height="smAndUp && !isShort ? 64 : 48"
              rounded
              role="button"
              tabindex="0"
              :aria-pressed="selectableKeyword.isActive(word.id)"
              :aria-label="word.keyword"
              @click="onKeywordClick(word.id)"
              @keydown.enter.prevent="onKeywordClick(word.id)"
              @keydown.space.prevent="onKeywordClick(word.id)"
            >
              <PlayNarrationButton
                v-if="rtlLanguage && getAudioId(word.keyword)"
                :audio-id="getAudioId(word.keyword)"
                :content-registry-url="contentRegistryUrl"
                :t="t"
              />
              <ScrollableOnHover :text="word.keyword" :rtl="rtlLanguage" class="flex-grow-1" />
              <PlayNarrationButton
                v-if="!rtlLanguage && getAudioId(word.keyword)"
                :audio-id="getAudioId(word.keyword)"
                :content-registry-url="contentRegistryUrl"
                :t="t"
              />
            </v-sheet>
          </v-fade-transition>
        </div>
      </div>
      <div v-else class="d-flex flex-column ga-3 ga-sm-10 w-100">
        <v-btn
          variant="outlined"
          :width="smAndUp ? keywordWidth : undefined"
          :height="smAndUp ? 72 : 56"
          class="thick-border text-none text-h6 text-lg-h5"
          @click="emit('restart')"
        >
          <v-icon icon="fa:fawsb fa-gamepad-modern" size="18" class="position-absolute left-0 ml-6" />
          {{ t('games.match_the_pairs.play_again') }}
        </v-btn>
        <v-btn
          variant="outlined"
          :width="smAndUp ? keywordWidth : undefined"
          :height="smAndUp ? 72 : 56"
          class="thick-border text-none text-h6 text-lg-h5"
          @click="emit('shuffle')"
        >
          <v-icon icon="fa:fawsb fa-arrows-rotate" size="18" class="position-absolute left-0 ml-6" />
          {{ t('games.match_the_pairs.shuffle_new') }}
        </v-btn>
        <v-btn
          variant="outlined"
          :width="smAndUp ? keywordWidth : undefined"
          :height="smAndUp ? 72 : 56"
          class="thick-border text-none text-h6 text-lg-h5"
          @click="emit('close')"
        >
          <v-icon icon="fa:fawsb fa-xmark" size="18" class="position-absolute left-0 ml-6" />
          {{ t('games.match_the_pairs.exit') }}
        </v-btn>
      </div>
    </v-col>

    <v-col :class="{ 'overflow-y-auto pa-4': !smAndUp }" class="d-flex flex-column">
      <v-row
        id="descriptions"
        v-click-outside="selectableDescription.clickOutside"
        class="flex-grow-1 align-content-start align-content-sm-stretch ma-n2 ma-lg-n4"
      >
        <v-col v-for="description in descriptions" :key="description.id" cols="12" sm="6" class="pa-2 pa-lg-4">
          <!-- eslint-disable-next-line vuejs-accessibility/interactive-supports-focus -->
          <v-card
            :id="description.id"
            variant="outlined"
            :style="cardStyle(description.id)"
            :class="{
              'opacity-50': selectedItem.type === 'description' && !selectableDescription.isActive(description.id),
              'cursor-pointer': !matchedWord(description.id),
            }"
            class="thick-border h-100 d-flex flex-column pa-3 pl-4"
            :min-height="smAndUp ? (isShort ? 110 : 160) : undefined"
            role="button"
            :tabindex="matchedWord(description.id) ? -1 : 0"
            :aria-pressed="selectableDescription.isActive(description.id)"
            :aria-label="description.description"
            @click="onDescriptionClick(description.id)"
            @keydown.enter.prevent="onDescriptionClick(description.id)"
            @keydown.space.prevent="onDescriptionClick(description.id)"
          >
            <p
              :dir="rtlLanguage ? 'rtl' : 'ltr'"
              :class="[
                description.description.length > LONG_DESCRIPTION_LENGTH || isShort
                  ? 'text-body-1 text-lg-h6'
                  : 'text-h6 text-lg-h5',
                { 'pl-12': rtlLanguage && getAudioId(description.description) },
                { 'pr-12': !rtlLanguage && getAudioId(description.description) },
              ]"
              class="mb-2"
              aria-hidden="true"
            >
              {{ description.description }}
            </p>
            <PlayNarrationButton
              v-if="getAudioId(description.description)"
              :audio-id="getAudioId(description.description)"
              :disabled="!!matchedWord(description.id)"
              :content-registry-url="contentRegistryUrl"
              :t="t"
              class="position-absolute top-0 ma-2"
              :class="[rtlLanguage ? 'left-0' : 'right-0']"
            />
            <v-fade-transition>
              <v-card
                v-if="matchedWord(description.id)"
                variant="outlined"
                width="250"
                max-width="100%"
                :height="smAndUp && !isShort ? 64 : 48"
                class="thick-border align-self-end mt-auto flex-shrink-0 d-flex align-center pl-4 pr-2 text-body-1 text-md-h6 text-lg-h5"
              >
                <PlayNarrationButton
                  v-if="rtlLanguage && getAudioId(matchedWord(description.id))"
                  :audio-id="getAudioId(matchedWord(description.id))"
                  :content-registry-url="contentRegistryUrl"
                  :t="t"
                />
                <ScrollableOnHover :text="matchedWord(description.id)" :rtl="rtlLanguage" class="flex-grow-1" />
                <PlayNarrationButton
                  v-if="!rtlLanguage && getAudioId(matchedWord(description.id))"
                  :audio-id="getAudioId(matchedWord(description.id))"
                  :content-registry-url="contentRegistryUrl"
                  :t="t"
                />
              </v-card>
            </v-fade-transition>
          </v-card>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
  <!-- Accessible live region for screen reader feedback -->
  <div ref="feedbackDiv" class="d-sr-only" role="log" aria-live="polite" aria-atomic="true"></div>
</template>

<style lang="scss" scoped>
.thick-border {
  border-width: 2px;
}
</style>
